import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule, Provider } from '@angular/core';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { es_ES, NZ_I18N } from 'ng-zorro-antd/i18n';
import { en_US } from 'ng-zorro-antd/i18n';
import { fr_FR } from 'ng-zorro-antd/i18n';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import es from '@angular/common/locales/es';
import fr from '@angular/common/locales/fr';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IconsProviderModule } from './icons-provider.module';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { Amplify } from 'aws-amplify';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import * as Sentry from '@sentry/angular-ivy';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { GoogleMapsModule } from '@angular/google-maps';
import AWS from 'aws-sdk/global';
import { NzNotificationModule } from 'ng-zorro-antd/notification';
import { Router } from '@angular/router';
import { PipesModule } from './pipes/pipes.module';
import { AmplifyAuthenticatorModule } from '@aws-amplify/ui-angular';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { SpeedTestModule } from 'ng-speed-test';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { createSentryCustomErrorHandler } from './sentry-custom-error-handler';
import { NzListModule } from 'ng-zorro-antd/list';
import { AlertModule } from './components/alert/alert.module';
import { ApiInterceptor } from './interceptors/api-interceptor';

AWS.config.update({
  region: environment.AWS_REGION,
  maxRetries: 1,
  httpOptions: {
    timeout: 0,
    connectTimeout: 3000,
  },
});

Amplify.configure({
  Auth: {
    Cognito: {
      identityPoolId: environment.IDENTITY_POOL_ID,
      userPoolId: environment.USER_POOL_ID,
      userPoolClientId: environment.USER_POOL_WEB_CLIENT_ID,
    },
  },
  API: {
    REST: {
      'fdm-api': {
        region: environment.AWS_REGION,
        endpoint: environment.API_URL,
      },
    },
  },
  Storage: {
    S3: {
      bucket: environment.RESTRICTED_BUCKET,
      region: environment.AWS_REGION,
    },
  },
});

registerLocaleData(en);
registerLocaleData(es);
registerLocaleData(fr);

const providers: Provider[] = [
  {
    provide: NZ_I18N,
    useFactory: (localId: string): any => {
      switch (localId) {
        case 'en':
          return en_US;
        /** keep the same with angular.json/i18n/locales configuration **/
        case 'es':
          return es_ES;
        case 'fr':
          return fr_FR;
        default:
          return en_US;
      }
    },
    deps: [LOCALE_ID],
  },
  {
    provide: ErrorHandler,
    useValue: createSentryCustomErrorHandler({
      showDialog: false,
      logErrors: true,
    }),
  },
  // we only use Sentry in production see also main.ts
  {
    provide: Sentry.TraceService,
    deps: [Router],
  },
  {
    provide: APP_INITIALIZER,
    useFactory: () => (): void => {
      //
    },
    deps: [Sentry.TraceService],
    multi: true,
  },
  /*{
    provide: APP_INITIALIZER,
    useFactory: AxiosConfigFactory,
    deps: [BackendApiInterceptor],
    multi: true,
  },*/
  { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
];

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    IconsProviderModule,
    NzLayoutModule,
    AmplifyAuthenticatorModule,
    NzMenuModule,
    NzModalModule,
    NzButtonModule,
    ServiceWorkerModule.register('custom-service-worker.js', { enabled: environment.production }),
    NzMessageModule,
    NzTypographyModule,
    NzBadgeModule,
    NzRadioModule,
    NzToolTipModule,
    GoogleMapsModule,
    NzNotificationModule,
    PipesModule,
    NzDrawerModule,
    SpeedTestModule,
    NzProgressModule,
    NzDividerModule,
    HammerModule,
    NzListModule,
    AlertModule,
  ],
  providers: [providers],
  bootstrap: [AppComponent],
})
export class AppModule {}
